import { useState } from "react";
import { Tabs, type Tab } from "@theorgchartdev/storybook";
import { planningTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";
import { SavedFilterHighlightList } from "./SavedFilterHighlightList";
import { SharedHighlights } from "./SharedHighlights";
import { appstr } from "@coreapp/utils/appstr";
import { enumFeaturesList } from "@coreapp/constants";
import { getFeatureAccess } from "@/Utils/Misc";
import { Globals } from "@coreapp/CoreApp/Globals";
import { useHighlightController } from "./useHighlightController";
import { MyHighlights } from "./MyHighlights";
import { HighlightsEditor } from "./HighlightsEditor";
import { FilterDetailsView } from "@/appComponents/Navigation/RightSidebar/Utils/FilterDetailsView";
import { useBlockerContext } from "@/providers/BlockerProvider";
import { QHighlightMgr } from "@coreapp/CoreApp/QHighlightMgr";
import { QHighlight } from "@coreapp/CoreApp/QHighlight";
import type { SharedFilterDoc } from "@/appComponents/Navigation/RightSidebar/FilterPanel/useSharedFilterController";
import { useTopic } from "@/hooks/useTopic";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";

type Props = {
  manager: QHighlightMgr;
  canShowInteractiveHighlights?: boolean;
  GetSavedContent: () => any[];
  ApplySavedContent: (obj: any) => (event: any) => void;
  GetNormalizedSavedList: () => Array<{
    name: string;
    id: string;
    enabled: boolean;
    color: string;
  }>;
};

export const HighlightsPanel = (props: Props) => {
  const highlightsCtrl = useHighlightController({ manager: props.manager });
  const { shouldBlock } = useBlockerContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [sharedActiveCount, setSharedActiveCount] = useState(0);
  const [ownerActiveCount, setOwnerActiveCount] = useState(0);
  const [editingSharedDoc, setEditingSharedDoc] = useState<SharedFilterDoc | null>(null);
  const [detailsView, setDetailsView] = useState<any>(null);
  const { addToast }: any = useToast();

  const handleEditSharedHighlight = (filter: SharedFilterDoc) => {
    const highlight = new QHighlight();
    highlight.LoadFromJSON(filter.definition);
    highlight.SetHighlightApplied(true);
    setEditingSharedDoc(filter);
    highlightsCtrl.handleStartEditing(highlight);
  };

  const handleStopEditingShared = () => {
    setEditingSharedDoc(null);
    highlightsCtrl.handleStopEditing();
  };

  const [returnTab, setReturnTab] = useState(0);

  useTopic("chartDocumentClosed", () => {
    setSharedActiveCount(0);
    setOwnerActiveCount(0);
  });

  const handleViewHighlightDetails = (data: any) => {
    setReturnTab(data.sourceTab === "shared" ? 1 : 0);
    setDetailsView(data);
  };

  if (detailsView) {
    return (
      <FilterDetailsView
        entityType="highlight"
        name={detailsView.name}
        ownerDisplayName={detailsView.ownerDisplayName}
        isShared={detailsView.isShared}
        userRole={detailsView.userRole}
        rules={detailsView.rules}
        onBack={() => {
          setCurrentTab(returnTab);
          setDetailsView(null);
        }}
        shareProps={
          detailsView.shareProps && !detailsView.shareProps.sharedDoc
            ? {
                ...detailsView.shareProps,
                onShareComplete: () => {
                  setDetailsView(null);
                  detailsView.shareProps.onShareComplete?.();
                }
              }
            : detailsView.shareProps
        }
        onEdit={
          detailsView.onEdit
            ? () => {
                setDetailsView(null);
                detailsView.onEdit();
              }
            : undefined
        }
        onCopy={
          detailsView.onCopy
            ? () => {
                detailsView.onCopy();
                setCurrentTab(0);
                setDetailsView(null);
                addToast({
                  ...(detailsView.sourceTab === "shared"
                    ? createBoldToastMessage("Shared highlight copied", {
                        entity: detailsView.name
                      })
                    : createBoldToastMessage("Entity copy created", { entity: detailsView.name })),
                  status: TOAST_STATUS.SUCCESS
                });
              }
            : undefined
        }
        onDelete={
          detailsView.onDelete
            ? () => {
                setDetailsView(null);
                detailsView.onDelete();
                addToast({
                  ...createBoldToastMessage("Entity deleted", { entity: detailsView.name }),
                  status: TOAST_STATUS.SUCCESS
                });
              }
            : undefined
        }
      />
    );
  }

  if (highlightsCtrl.highlightEditorState) {
    return (
      <HighlightsEditor
        highLightCtrl={highlightsCtrl}
        sharedFilterDoc={editingSharedDoc}
        onStopEditingShared={handleStopEditingShared}
      />
    );
  }

  const handleTabChange = (index: number, ignoreBlocker = false) => {
    if (!ignoreBlocker) {
      const onProceed = () => handleTabChange(index, true);
      if (shouldBlock("rightSidebarChangeMode", onProceed)) {
        return;
      }
    }

    setCurrentTab(index);
  };

  const getTabs = () => {
    const highlightTabs: Tab[] = [];

    // My highlights (formerly Interactive)
    if (props.canShowInteractiveHighlights) {
      const numberOfInterHighlights =
        highlightsCtrl.getNumberOfActiveHighlights() + ownerActiveCount;
      highlightTabs.push({
        header: appstr("My highlights"),
        content: (
          <MyHighlights
            highlightsCtrl={highlightsCtrl}
            onEditSharedHighlight={handleEditSharedHighlight}
            onOwnerActiveCountChange={setOwnerActiveCount}
            onViewDetails={handleViewHighlightDetails}
          />
        ),
        dataTestId: planningTestId("highlight", "myhighlights-tab") ?? "rsb-highlights-interactive",
        badgeProps: numberOfInterHighlights
          ? {
              badgeContent: numberOfInterHighlights.toString(),
              color: "success",
              tooltipProps: {
                message: appstr("Number of entity applied to the chart", {
                  entity: appstr("Interactive highlights").toLowerCase()
                }),
                position: "top-start"
              }
            }
          : undefined
      });
    }
    // Shared highlights tab (feature-gated)
    if (Globals._uiConfig.fShareFilters) {
      highlightTabs.push({
        header: appstr("Shared"),
        content: (
          <SharedHighlights
            onActiveCountChange={setSharedActiveCount}
            onEditSharedHighlight={handleEditSharedHighlight}
            onCopyHighlight={(definition: any) => {
              highlightsCtrl.handleCopyHighlight(definition);
              setCurrentTab(0);
            }}
            onCopyAndEditHighlight={(definition: any) => {
              highlightsCtrl.handleCopyAndEditHighlight(definition);
              setCurrentTab(0);
            }}
            onViewDetails={handleViewHighlightDetails}
          />
        ),
        dataTestId: planningTestId("highlight", "shared-tab") ?? "rsb-highlights-shared",
        badgeProps: sharedActiveCount
          ? {
              badgeContent: sharedActiveCount.toString(),
              color: "success",
              tooltipProps: {
                message: appstr("Number of entity applied to the chart", {
                  entity: appstr("Shared highlights").toLowerCase()
                }),
                position: "top-start"
              }
            }
          : undefined
      });
    }

    // Legacy (Saved) highlights — only show tab when saved highlights exist
    const numberOfSavedHighlights = props?.manager.GetNumberOfSavedHighlightsApplied();
    const savedHighlightCount = props.GetSavedContent()?.length ?? 0;
    const hasSavedHighlights = getFeatureAccess(enumFeaturesList.savedHighlights);
    if (hasSavedHighlights && savedHighlightCount > 0) {
      highlightTabs.push({
        header: appstr("Legacy"),
        content: (
          <SavedFilterHighlightList
            type="highlights"
            ApplySavedContent={props.ApplySavedContent}
            GetNormalizedSavedList={props.GetNormalizedSavedList}
            GetSavedContent={props.GetSavedContent}
            includeHighlightsInfo={true}
            showHeader={false}
          />
        ),
        disabled: !hasSavedHighlights,
        dataTestId: planningTestId("highlight", "saved-tab") ?? "rsb-highlights-saved",
        badgeProps: numberOfSavedHighlights
          ? {
              badgeContent: numberOfSavedHighlights.toString(),
              color: "success",
              tooltipProps: {
                message: appstr("Number of entity applied to the chart", {
                  entity: appstr("Saved highlights").toLowerCase()
                }),
                position: "top-start"
              }
            }
          : undefined
      });
    }

    return highlightTabs;
  };

  const tabs = getTabs();
  if (tabs.length === 0) return null;

  return (
    <Tabs
      tabSize={"medium"}
      customClass={"ocn-sidebar-tabs"}
      customHeaderClass={"oc-tab-header-no-divider"}
      customContentClass={"ocn-chart-explorer-tabs-content"}
      tabIndex={currentTab}
      onTabChange={handleTabChange}
      tabs={tabs}
    />
  );
};
